import { useMemo, useState } from 'react';
import PhoneResultCard from './PhoneResultCard';
import PhoneMetadataSkeleton from '../../components/investigation/PhoneMetadataSkeleton';
import CategoryTabs from '../../components/investigation/CategoryTabs';
import TargetPivotsCard from '../../components/investigation/TargetPivotsCard';
import { deepLinkOptsFromPivot, openInvestigationInNewTab } from '../../utils/investigationDeepLink';

/** @typedef {'all'|'summary'|'discovery'|'web'} PhoneFeedFilterId */

export const PHONE_FEED_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'summary', label: 'Risk Summary' },
  { id: 'discovery', label: 'Discovery' },
  { id: 'web', label: 'Web & Mentions' },
];

function showPhoneSection(activeFilter, sectionId) {
  return activeFilter === 'all' || activeFilter === sectionId;
}

/** Stable feed order for non-summary rows: identity (legacy) → web. */
function phoneResultTier(res) {
  if (res.type === 'phone_identity' || res.source === 'whatsapp') return 0;
  return 1;
}

/**
 * Fold legacy phone_identity into phone_metadata when both are present (older caches).
 * New scans emit a single Phone Risk Summary row — without Owner verdicts.
 */
function coalescePhoneSummaryRows(results) {
  const list = Array.isArray(results) ? [...results] : [];
  const metaIdx = list.findIndex((r) => r?.source === 'phone_metadata');
  const idIdx = list.findIndex((r) => r?.source === 'phone_identity' || r?.type === 'phone_identity');
  if (metaIdx < 0 || idIdx < 0 || metaIdx === idIdx) return list;
  const meta = { ...list[metaIdx], details: { ...(list[metaIdx].details || {}) } };
  const ident = list[idIdx];
  if (ident?.details?.voip_verified_identity != null) {
    meta.details.voip_verified_identity = ident.details.voip_verified_identity;
  }
  const tags = [...(meta.matched_tags || [])].filter((t) => !String(t).startsWith('Owner:'));
  for (const t of ident?.matched_tags || []) {
    if (t && !String(t).startsWith('Owner:') && !tags.includes(t)) tags.push(t);
  }
  meta.matched_tags = tags;
  delete meta.details.owner_name;
  if (!meta.audit && ident?.audit) meta.audit = ident.audit;
  const out = list.filter((_, i) => i !== idIdx);
  out[metaIdx > idIdx ? metaIdx - 1 : metaIdx] = meta;
  return out;
}

function sortSecondaryPhoneResults(results) {
  return [...results]
    .map((r, streamIndex) => ({ r, streamIndex }))
    .sort((a, b) => {
      const ta = phoneResultTier(a.r);
      const tb = phoneResultTier(b.r);
      if (ta !== tb) return ta - tb;
      return a.streamIndex - b.streamIndex;
    })
    .map(({ r }) => r);
}

const PhoneIntelligence = ({
  displayResults,
  loading,
  getDisplayName,
  extractedLeads,
  onEmailAnalysis,
  onPivotToPerson = null,
  onPivotLead = null,
}) => {
  const [activeFilter, setActiveFilter] = useState(/** @type {PhoneFeedFilterId} */ ('all'));

  const coalesced = useMemo(
    () => coalescePhoneSummaryRows(displayResults || []),
    [displayResults],
  );

  const metadataRes = useMemo(
    () => coalesced.find((r) => r?.source === 'phone_metadata') || null,
    [coalesced],
  );

  const hibpStatus = useMemo(
    () => coalesced.find((r) => r?.source === 'hibp_status') || null,
    [coalesced],
  );

  const secondaryResults = useMemo(() => {
    const rest = coalesced.filter(
      (r) =>
        r &&
        r.source !== 'phone_metadata' &&
        r.source !== 'hibp_status' &&
        !(r.source === 'phone_identity' || r.type === 'phone_identity'),
    );
    return sortSecondaryPhoneResults(rest);
  }, [coalesced]);

  const webCards = useMemo(
    () => (secondaryResults || []).filter((r) => Boolean(getDisplayName?.(r))),
    [secondaryResults, getDisplayName],
  );

  const discoveryCount = useMemo(() => {
    const pivots = Array.isArray(extractedLeads?.pivots) ? extractedLeads.pivots.length : 0;
    const emails = Array.isArray(extractedLeads?.emails) ? extractedLeads.emails.length : 0;
    return pivots > 0 ? pivots : emails;
  }, [extractedLeads]);

  const summaryCount = metadataRes || loading ? 1 : 0;
  const webCount = webCards.length;

  const filterCounts = useMemo(
    () => ({
      all: summaryCount + discoveryCount + webCount,
      summary: summaryCount,
      discovery: discoveryCount,
      web: webCount,
    }),
    [summaryCount, discoveryCount, webCount],
  );

  const handlePivotLead = (pivot) => {
    if (typeof onPivotLead === 'function') {
      onPivotLead(pivot);
      return;
    }
    const opts = deepLinkOptsFromPivot(pivot);
    if (!opts?.query) return;
    openInvestigationInNewTab(opts);
  };

  const showMetadataSlot = Boolean(metadataRes) || loading;
  const targetPivots = useMemo(() => {
    const pivots = Array.isArray(extractedLeads?.pivots) ? extractedLeads.pivots : [];
    if (pivots.length > 0) return pivots;
    const emails = Array.isArray(extractedLeads?.emails) ? extractedLeads.emails : [];
    return emails;
  }, [extractedLeads]);
  const hasPivots = targetPivots.length > 0;
  const showMetaColumn = showPhoneSection(activeFilter, 'summary') && showMetadataSlot;
  const showPivotsColumn = showPhoneSection(activeFilter, 'discovery') && hasPivots;
  const useSplitLandscape = showMetaColumn && showPivotsColumn;
  const metadataFullWidth = !hasPivots;

  if (!showMetadataSlot && secondaryResults.length === 0 && !hasPivots) {
    return null;
  }

  return (
    <div className="space-y-3 text-sm">
      <CategoryTabs
        filters={PHONE_FEED_FILTERS}
        activeFilter={activeFilter}
        onFilterChange={setActiveFilter}
        counts={filterCounts}
        ariaLabel="Phone result categories"
        testId="phone-feed-filters"
      />

      {showMetaColumn || showPivotsColumn ? (
        <div
          className={`phone-meta-pivots-row grid w-full grid-cols-1 items-start gap-3 ${
            useSplitLandscape
              ? 'phone-meta-pivots-row--split md:landscape:grid-cols-2'
              : 'grid-cols-1'
          }`}
          data-testid="phone-meta-pivots-row"
          data-split={useSplitLandscape ? 'true' : 'false'}
        >
          {showMetaColumn ? (
            <div
              className={`phone-metadata-card min-h-[9.5rem] ${metadataFullWidth ? 'w-full' : ''}`}
              data-testid="phone-metadata-slot"
            >
              {metadataRes ? (
                <PhoneResultCard
                  res={metadataRes}
                  name={getDisplayName(metadataRes) || 'Phone Risk Summary'}
                  onPivotToPerson={onPivotToPerson}
                  hibpStatus={hibpStatus}
                  fullWidthMetrics={metadataFullWidth}
                />
              ) : (
                <PhoneMetadataSkeleton />
              )}
            </div>
          ) : null}

          {showPivotsColumn ? (
            <TargetPivotsCard extractedLeads={extractedLeads} onPivotLead={handlePivotLead} />
          ) : null}
        </div>
      ) : null}

      {showPhoneSection(activeFilter, 'web')
        ? webCards.map((res, idx) => {
            const name = getDisplayName(res);
            return (
              <PhoneResultCard
                key={`phone-card-${idx}-${res.source}-${res.type}-${res.url || name}`}
                res={res}
                name={name}
                onPivotToPerson={onPivotToPerson}
              />
            );
          })
        : null}
    </div>
  );
};

export default PhoneIntelligence;
export { PhoneResultCard };
